/**
 * Selector-parity — legacy's `.model_field` DOM sweep.
 *
 * Several Backbone rating dialogs build their Select payload as an object
 * literal and THEN run a loop over every registered input, copying the ones
 * carrying the `model_field` class onto that payload as `+value`:
 *
 *   for (let i in self.inputsDOM) {
 *     let value = …input.getValue ? …getValue() : …input.value;
 *     if ($.inArray('model_field', …input.classList) != -1)
 *       self.model_data.models_field[i] = +value;
 *   }
 *
 * — ewheel.selector.js:2054-2066, uvc.selector.js:1651-1656, and the same
 * shape in filter.selector.js.
 *
 * This loop is the ONLY route to the server for any control that the literal
 * does not also name, and it is invisible to anyone porting the literal alone.
 * That is how the energy wheel lost `oa_sp` / `ra_sp` / `oa_db_temp_winter` /
 * its four `*_air_stream_*_id` keys and UVC lost `powered_by_id` — every one
 * of them rendered, editable, and silently discarded on Select.
 *
 * `self.inputsDOM` is built from `self.$el[0].querySelectorAll(…)`, i.e. the
 * whole dialog including inactive tab panes — the React dialogs likewise keep
 * every pane mounted (`tab-pane fade` plus a conditional `in active`), so
 * scoping to the selector's own root element reproduces it exactly.
 *
 * ★ Legacy's loop runs LAST and so OVERRIDES the literal for every name it
 * touches. That was first ported as an additive merge on the assumption that
 * the override is a no-op on dev (the inputs having just been re-rendered from
 * the rated row). It is not: the rated row echoes back only the leaving-air
 * side, so for the entering conditions the DOM is the only source. Measured on
 * dev's own database — an Innergytech wheel selected there
 * (`ksdm_ahu_component_energy_wheel.component_id = 24599`, rated
 * 2026.09.08_11:59:11) stores `oa_rh_winter = 20`, `ra_db_temp_winter = 75`,
 * `ra_rh_winter = 63`, `ra_airflow_actual_winter = 6000`, all of which the
 * rated row supplies as null. Hence `override`, which the energy wheel passes.
 *
 * Callers that do NOT pass it stay additive: on those dialogs the literal
 * already sources the swept names from the same state the inputs render from,
 * so the two agree and only the names the literal omits are in play.
 */
export function sweepModelFieldInputs(
  root: HTMLElement | null,
  /** legacy `!i.includes('wb_temp')` on the wheel; other dialogs sweep the
   * lot. Names for which this returns true are skipped. */
  skip: (name: string) => boolean = () => false
): Record<string, number> {
  const out: Record<string, number> = {};
  if (!root) return out;
  root
    .querySelectorAll<HTMLInputElement | HTMLSelectElement>(
      'input.model_field[name], select.model_field[name]'
    )
    .forEach((el) => {
      const name = el.name;
      if (!name || skip(name)) return;
      const raw = el.value;
      if (raw == null || raw === '') return;
      // the inputs render through the display formatters, which group
      // thousands; legacy reads past that with the formatter's own getValue(),
      // so stripping the separator is the equivalent.
      const n = Number(String(raw).replace(/,/g, ''));
      if (!Number.isNaN(n)) out[name] = n;
    });
  return out;
}

/**
 * Merge a sweep result into a payload. `override` reproduces legacy's
 * `models_field[i] = +value` (last write wins); without it the merge is
 * additive and never clobbers a key the literal already produced.
 */
export function mergeSweptFields(
  payload: Record<string, unknown>,
  swept: Record<string, number>,
  override = false
): void {
  for (const [k, v] of Object.entries(swept)) {
    if (override || !(k in payload)) payload[k] = v;
  }
}
